import { GenericSelect } from '@cmp-src/primitives/Select/select-controls/GenericSelect';
import type { GenericSelectProps, SelectOption } from '@cmp-src/primitives/Select/select-controls/GenericSelect';
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@gettactic/helpers';
import { MagnifyingGlassIcon } from '@cmp-src/icons/TacticIcons/MagnifyingGlassIcon';
import { ChevronDown } from 'lucide-react';

export type FilterOption = SelectOption;

const EmployeeSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    hasValue?: boolean;
  }
>(({ className, children, hasValue = false, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 min-h-[42px] w-full cursor-pointer items-center gap-2 bg-background py-2 pr-3 font-bold text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      {hasValue ? (
        <ChevronDown className="h-4 w-4" />
      ) : (
        <MagnifyingGlassIcon className="h-4 w-4" />
      )}
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
EmployeeSelectTrigger.displayName = 'EmployeeSelectTrigger';

export function UserFilter({
  options,
  onCancel,
  onChange,
  onInputChange,
  defaultValue,
  placeholder = 'Select options',
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  disabled = false,
  applyOnClick = false
}: {
  options: FilterOption[];
  onCancel: GenericSelectProps['onCancel'];
  onChange: GenericSelectProps['onChange'];
  onInputChange: GenericSelectProps['onInputChange'];
  defaultValue?: string | string[];
  placeholder?: string;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  disabled?: boolean;
  applyOnClick?: boolean;
}) {
  const handleApply = (values: string | string[]) => {
    onChange(values);
  };

  const hasValue = React.useMemo(() => {
    if (Array.isArray(defaultValue)) {
      return defaultValue.length > 0;
    }
    return !!defaultValue;
  }, [defaultValue]);

  return (
    <GenericSelect
      options={options}
      onCancel={onCancel}
      onChange={(values) => {
        // We don't need to update local state since we're just passing through
        // No-op to prevent unwanted onChange calls during selection
      }}
      onInputChange={onInputChange}
      defaultValue={defaultValue}
      placeholder={hasValue ? placeholder : "Employee"}
      isMultiSelect={true}
      showApplyButton={true}
      onApply={handleApply}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      triggerComponent={<EmployeeSelectTrigger disabled={disabled} hasValue={hasValue} />}
      showSearch={true}
      disabled={disabled}
      applyOnClick={applyOnClick}
      triggerClassName="font-bold text-muted-foreground"
    />
  );
}
